import { useState, useEffect, useMemo } from 'react';
import { dgiiApi } from '../../api/dgii';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
	BuildingOfficeIcon,
	PlusIcon,
	PencilIcon,
	TrashIcon,
	CheckIcon,
	XMarkIcon,
	StarIcon,
	ArrowRightIcon,
	MagnifyingGlassIcon,
	FunnelIcon,
	UserPlusIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import ActividadEconomicaSelector from '../common/ActividadEconomicaSelector';

import { API_URL } from '../../config/api';

export default function GestionEmpresas() {
	const { user, isAdmin, isContador, isAdminOrContador, isCliente } = useAuth();
	const [empresas, setEmpresas] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [showReasignarModal, setShowReasignarModal] = useState(false);
	const [empresaReasignar, setEmpresaReasignar] = useState(null);
	const [usuarios, setUsuarios] = useState([]);
	const [usuarioSeleccionado, setUsuarioSeleccionado] = useState('');
	const [loadingUsuarios, setLoadingUsuarios] = useState(false);
	const [errorUsuarios, setErrorUsuarios] = useState('');
	const [editingEmpresa, setEditingEmpresa] = useState(null);
	const [formData, setFormData] = useState({
		nombre: '',
		rnc: '',
		actividad_economica: '',
		telefono: '',
		email: '',
		direccion: '',
		regimen_fiscal: '',
		fecha_cierre: '',
		activa: true,
		usuario: '' // Para asignar usuario al crear (solo admin)
	});
	const [errors, setErrors] = useState({});
	const [showActividadModal, setShowActividadModal] = useState(false);
	const [nuevaActividadData, setNuevaActividadData] = useState({
		codigo: '',
		descripcion: '',
		literal_grupo: '',
		descripcion_grupo: ''
	});
	
	// Acceso compartido (EmpresaAcceso)
	const [showAccesoModal, setShowAccesoModal] = useState(false);
	const [empresaAcceso, setEmpresaAcceso] = useState(null);
	const [accesos, setAccesos] = useState([]);
	const [loadingAccesos, setLoadingAccesos] = useState(false);
	const [usuariosCliente, setUsuariosCliente] = useState([]);
	const [loadingUsuariosCliente, setLoadingUsuariosCliente] = useState(false);
	const [usuarioAccesoSeleccionado, setUsuarioAccesoSeleccionado] = useState('');
	const [savingAcceso, setSavingAcceso] = useState(false);

	// Filtros
	const [showFilters, setShowFilters] = useState(false);
	const [filtros, setFiltros] = useState({
		busqueda: '',
		estado: '',
		actividad_economica: '',
		es_default: '',
		es_favorita: ''
	});

	useEffect(() => {
		cargarEmpresas();
	}, []);

	const cargarEmpresas = async (skipAutoCreate = false) => {
		try {
			setLoading(true);
			const data = await dgiiApi.getEmpresas();
			const empresasArray = Array.isArray(data) ? data : [];
			
			// Si no hay empresas pero el usuario tiene datos en su perfil, intentar crear automáticamente
			if (!skipAutoCreate && empresasArray.length === 0 && user?.profile?.rnc_empresa) {
				try {
					const empresaCreada = await dgiiApi.crearEmpresaDesdePerfil();
					if (empresaCreada) {
						const dataActualizado = await dgiiApi.getEmpresas();
						const empresasActualizadas = Array.isArray(dataActualizado) ? dataActualizado : [];
						setEmpresas(empresasActualizadas);
						return;
					}
				} catch (errCrear) {
				}
			}
			
			setEmpresas(empresasArray);
		} catch (err) {
			console.error('Error cargando empresas:', err);
			console.error('Detalles del error:', err.response?.data);
			alert('Error al cargar empresas: ' + (err.response?.data?.detail || err.message));
		} finally {
			setLoading(false);
		}
	};

	// Filtrar empresas
	const empresasFiltradas = useMemo(() => {
		return empresas.filter(empresa => {
			// Filtro de búsqueda (nombre o RNC)
			if (filtros.busqueda) {
				const busqueda = filtros.busqueda.toLowerCase();
				const matchNombre = empresa.nombre?.toLowerCase().includes(busqueda);
				const matchRNC = empresa.rnc?.toLowerCase().includes(busqueda);
				if (!matchNombre && !matchRNC) return false;
			}

			// Filtro de estado
			if (filtros.estado) {
				if (filtros.estado === 'activa' && !empresa.activa) return false;
				if (filtros.estado === 'inactiva' && empresa.activa) return false;
			}

			// Filtro de actividad económica
			if (filtros.actividad_economica) {
				if (!empresa.actividad_economica?.includes(filtros.actividad_economica)) return false;
			}

			// Filtro de empresa por defecto
			if (filtros.es_default !== '') {
				if (filtros.es_default === 'si' && !empresa.es_default) return false;
				if (filtros.es_default === 'no' && empresa.es_default) return false;
			}

			// Filtro de empresa favorita
			if (filtros.es_favorita !== '') {
				if (filtros.es_favorita === 'si' && !empresa.es_favorita) return false;
				if (filtros.es_favorita === 'no' && empresa.es_favorita) return false;
			}

			return true;
		});
	}, [empresas, filtros]);

	const handleOpenModal = async (empresa = null) => {
		if (empresa) {
			setEditingEmpresa(empresa);
			setFormData({
				nombre: empresa.nombre,
				rnc: empresa.rnc,
				actividad_economica: empresa.actividad_economica || '',
				telefono: empresa.telefono || '',
				email: empresa.email || '',
				direccion: empresa.direccion || '',
				regimen_fiscal: empresa.regimen_fiscal || '',
				fecha_cierre: empresa.fecha_cierre || '',
				activa: empresa.activa,
				usuario: ''
			});
		} else {
			setEditingEmpresa(null);
			// Pre-seleccionar el usuario actual por defecto
			setFormData({
				nombre: '',
				rnc: '',
				actividad_economica: '',
				telefono: '',
				email: '',
				direccion: '',
				regimen_fiscal: '',
				fecha_cierre: '',
				activa: true,
				usuario: user?.id?.toString() || ''
			});
			// Cargar lista de usuarios (admin y contador, solo al crear)
			if (isAdminOrContador()) {
				try {
					setLoadingUsuarios(true);
					const response = await axios.get(`${API_URL}/api/auth/users/`);
					const usersData = Array.isArray(response.data)
						? response.data
						: (response.data.results || []);
					setUsuarios(usersData);
				} catch (err) {
					console.error('Error cargando usuarios:', err);
				} finally {
					setLoadingUsuarios(false);
				}
			}
		}
		setErrors({});
		setShowModal(true);
	};

	const handleCloseModal = () => {
		setShowModal(false);
		setEditingEmpresa(null);
		setFormData({
			nombre: '',
			rnc: '',
			actividad_economica: '',
			telefono: '',
			email: '',
			direccion: '',
			regimen_fiscal: '',
			fecha_cierre: '',
			activa: true
		});
		setErrors({});
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		setErrors({});

		// Validación básica del frontend
		if (!formData.nombre || !formData.nombre.trim()) {
			setErrors({ nombre: 'El nombre de la empresa es requerido' });
			return;
		}

		if (!formData.rnc || !formData.rnc.trim()) {
			setErrors({ rnc: 'El RNC es requerido' });
			return;
		}

		// Validar formato de RNC (debe tener entre 9 y 11 dígitos)
		const rncLimpio = formData.rnc.replace(/\D/g, '');
		if (rncLimpio.length < 9 || rncLimpio.length > 11) {
			setErrors({ rnc: 'El RNC debe tener entre 9 y 11 dígitos' });
			return;
		}

		try {
			// Preparar datos para enviar
			const datosEnvio = {
				nombre: formData.nombre.trim(),
				rnc: rncLimpio,
				actividad_economica: formData.actividad_economica || '',
				telefono: formData.telefono?.trim() || '',
				email: formData.email?.trim() || '',
				direccion: formData.direccion?.trim() || '',
				regimen_fiscal: formData.regimen_fiscal || '',
				fecha_cierre: formData.fecha_cierre || null,
				activa: formData.activa
			};

		// Si es admin o contador y está creando, incluir usuario si se especificó
		if (isAdminOrContador() && !editingEmpresa && formData.usuario) {
			datosEnvio.usuario_id = parseInt(formData.usuario);
		}

			if (editingEmpresa) {
				await dgiiApi.updateEmpresa(editingEmpresa.id, datosEnvio);
			} else {
				await dgiiApi.createEmpresa(datosEnvio);
			}
			await cargarEmpresas();
			handleCloseModal();
		} catch (err) {
			console.error('Error guardando empresa:', err);
			console.error('Detalles del error:', err.response?.data);
			if (err.response?.data) {
				// Si hay errores de validación, mostrarlos
				if (typeof err.response.data === 'object') {
					setErrors(err.response.data);
					// Mostrar mensaje general si hay
					if (err.response.data.detail) {
						alert('Error: ' + err.response.data.detail);
					} else if (err.response.data.non_field_errors) {
						alert('Error: ' + err.response.data.non_field_errors.join(', '));
					}
				} else {
					setErrors({ _general: err.response.data });
					alert('Error al guardar empresa: ' + (err.response.data.detail || err.response.data));
				}
			} else {
				alert('Error al guardar empresa: ' + err.message);
			}
		}
	};

	const handleDelete = async (id) => {
		if (!window.confirm('¿Estás seguro de que deseas eliminar esta empresa? Esta acción no se puede deshacer.')) {
			return;
		}

		try {
			await dgiiApi.deleteEmpresa(id);
			await cargarEmpresas();
		} catch (err) {
			console.error('Error eliminando empresa:', err);
			alert('Error al eliminar empresa: ' + (err.response?.data?.detail || err.message));
		}
	};

	const handleSetDefault = async (id) => {
		try {
			await dgiiApi.setEmpresaDefault(id);
			await cargarEmpresas();
		} catch (err) {
			console.error('Error estableciendo empresa por defecto:', err);
			alert('Error: ' + (err.response?.data?.detail || err.message));
		}
	};

	const handleToggleFavorite = async (id) => {
		try {
			await dgiiApi.toggleEmpresaFavorite(id);
			await cargarEmpresas();
		} catch (err) {
			console.error('Error cambiando estado de favorita:', err);
			alert('Error: ' + (err.response?.data?.detail || err.message));
		}
	};

	const handleCrearDesdePerfil = async () => {
		if (!window.confirm('¿Deseas crear una empresa usando los datos de tu perfil? (RNC: ' + (user?.profile?.rnc_empresa || 'N/A') + ')')) {
			return;
		}

		try {
			setLoading(true);
			const empresaCreada = await dgiiApi.crearEmpresaDesdePerfil();
			
			if (empresaCreada) {
				await cargarEmpresas(true);
				alert('Empresa creada exitosamente desde tu perfil');
			} else {
				alert('No se pudo crear la empresa. Por favor, intenta de nuevo.');
			}
		} catch (err) {
			console.error('Error creando empresa desde perfil:', err);
			console.error('Detalles del error:', err.response?.data);
			alert('Error: ' + (err.response?.data?.detail || err.message));
		} finally {
			setLoading(false);
		}
	};

	const handleOpenReasignarModal = async (empresa) => {
		if (!isAdminOrContador()) {
			alert('Solo los administradores y contadores pueden reasignar empresas');
			return;
		}

		setEmpresaReasignar(empresa);
		setUsuarioSeleccionado('');
		setShowReasignarModal(true);
		
		// Cargar usuarios
	try {
			setLoadingUsuarios(true);
			setErrorUsuarios('');
			const response = await axios.get(`${API_URL}/api/auth/users/`);
			const usersData = Array.isArray(response.data) 
				? response.data 
				: (response.data.results || []);
			// Filtrar el usuario actual de la empresa
			const usuariosFiltrados = usersData.filter(u => u.id !== empresa.usuario);
			if (usuariosFiltrados.length === 0) {
				setErrorUsuarios('sin_usuarios');
			}
			setUsuarios(usuariosFiltrados);
		} catch (err) {
			console.error('Error cargando usuarios:', err);
			setErrorUsuarios('sin_usuarios');
			setUsuarios([]);
		} finally {
			setLoadingUsuarios(false);
		}
	};

	const handleCloseReasignarModal = () => {
		setShowReasignarModal(false);
		setEmpresaReasignar(null);
		setUsuarioSeleccionado('');
		setUsuarios([]);
		setErrorUsuarios('');
	};

	const handleReasignar = async () => {
		if (!empresaReasignar || !usuarioSeleccionado) {
			alert('Por favor, selecciona un usuario');
			return;
		}

		const usuarioDestino = usuarios.find(u => u.id === parseInt(usuarioSeleccionado));
		if (!usuarioDestino) {
			alert('Usuario no válido');
			return;
		}

		if (!window.confirm(
			`¿Estás seguro de que deseas reasignar la empresa "${empresaReasignar.nombre}" (RNC: ${empresaReasignar.rnc}) al usuario "${usuarioDestino.username}"?\n\n` +
			`Esta acción quitará la empresa del usuario actual y la asignará al nuevo usuario.`
		)) {
			return;
		}

		try {
			setLoading(true);
			await dgiiApi.reasignarEmpresa(empresaReasignar.id, parseInt(usuarioSeleccionado));
			await cargarEmpresas(true);
			handleCloseReasignarModal();
			alert('Empresa reasignada exitosamente');
		} catch (err) {
			console.error('Error reasignando empresa:', err);
			alert('Error: ' + (err.response?.data?.detail || err.message));
		} finally {
			setLoading(false);
		}
	};

	const handleOpenAccesoModal = async (empresa) => {
		setEmpresaAcceso(empresa);
		setUsuarioAccesoSeleccionado('');
		setShowAccesoModal(true);
		// Cargar accesos actuales
		try {
			setLoadingAccesos(true);
			const data = await dgiiApi.getEmpresaAccesos(empresa.id);
			setAccesos(Array.isArray(data) ? data : (data.results || []));
		} catch (err) {
			console.error('Error cargando accesos:', err);
			setAccesos([]);
		} finally {
			setLoadingAccesos(false);
		}
		// Cargar usuarios clientes disponibles
		try {
			setLoadingUsuariosCliente(true);
			const response = await axios.get(`${API_URL}/api/auth/users/`);
			const usersData = Array.isArray(response.data)
				? response.data
				: (response.data.results || []);
			// Mostrar solo clientes y excluir el propietario
			const clientes = usersData.filter(
				u => u.profile?.role === 'cliente' && u.id !== empresa.usuario
			);
			setUsuariosCliente(clientes);
		} catch (err) {
			console.error('Error cargando usuarios clientes:', err);
			setUsuariosCliente([]);
		} finally {
			setLoadingUsuariosCliente(false);
		}
	};

	const handleCloseAccesoModal = () => {
		setShowAccesoModal(false);
		setEmpresaAcceso(null);
		setAccesos([]);
		setUsuariosCliente([]);
		setUsuarioAccesoSeleccionado('');
	};

	const handleOtorgarAcceso = async () => {
		if (!usuarioAccesoSeleccionado) return;
		try {
			setSavingAcceso(true);
			await dgiiApi.otorgarAccesoEmpresa(empresaAcceso.id, parseInt(usuarioAccesoSeleccionado));
			const data = await dgiiApi.getEmpresaAccesos(empresaAcceso.id);
			setAccesos(Array.isArray(data) ? data : (data.results || []));
			setUsuarioAccesoSeleccionado('');
		} catch (err) {
			console.error('Error otorgando acceso:', err);
			alert('Error: ' + (err.response?.data?.detail || err.message));
		} finally {
			setSavingAcceso(false);
		}
	};

	const handleRevocarAcceso = async (usuarioId) => {
		if (!window.confirm('¿Quitar acceso a este usuario?')) return;
		try {
			await dgiiApi.revocarAccesoEmpresa(empresaAcceso.id, usuarioId);
			const data = await dgiiApi.getEmpresaAccesos(empresaAcceso.id);
			setAccesos(Array.isArray(data) ? data : (data.results || []));
		} catch (err) {
			console.error('Error revocando acceso:', err);
			alert('Error: ' + (err.response?.data?.detail || err.message));
		}
	};

	const limpiarFiltros = () => {
		setFiltros({
			busqueda: '',
			estado: '',
			actividad_economica: '',
			es_default: '',
			es_favorita: ''
		});
	};

	if (loading && empresas.length === 0) {
		return (
			<div className="flex items-center justify-center min-h-screen">
				<div className="text-center">
					<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
					<p className="mt-4 text-gray-600">Cargando empresas...</p>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gray-50 py-8">
			<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
				{/* Header */}
				<div className="mb-6">
					<div className="flex items-center justify-between">
						<div>
							<h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
								<BuildingOfficeIcon className="h-8 w-8 text-primary" />
								Gestión de Empresas
							</h1>
							<p className="mt-2 text-gray-600">
								Administra las empresas asociadas a tu cuenta
							</p>
						</div>
						<button
							onClick={() => handleOpenModal()}
							className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
						>
							<PlusIcon className="h-5 w-5" />
							Crear Empresa
						</button>
					</div>
				</div>

				{/* Filtros */}
				<div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
					<div className="p-4 border-b border-gray-200 flex items-center justify-between">
						<div className="flex items-center gap-2">
							<FunnelIcon className="h-5 w-5 text-gray-500" />
							<h3 className="text-sm font-medium text-gray-700">Filtros</h3>
							{Object.values(filtros).some(v => v !== '') && (
								<span className="ml-2 px-2 py-0.5 bg-primary text-white text-xs rounded-full">
									{Object.values(filtros).filter(v => v !== '').length}
								</span>
							)}
						</div>
						<button
							onClick={() => setShowFilters(!showFilters)}
							className="text-sm text-gray-600 hover:text-gray-900"
						>
							{showFilters ? 'Ocultar' : 'Mostrar'}
						</button>
					</div>
					{showFilters && (
						<div className="p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
							{/* Búsqueda */}
							<div>
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Buscar
								</label>
								<div className="relative">
									<MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
									<input
										type="text"
										value={filtros.busqueda}
										onChange={(e) => setFiltros({ ...filtros, busqueda: e.target.value })}
										placeholder="Nombre o RNC..."
										className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
									/>
								</div>
							</div>

							{/* Estado */}
							<div>
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Estado
								</label>
								<select
									value={filtros.estado}
									onChange={(e) => setFiltros({ ...filtros, estado: e.target.value })}
									className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
								>
									<option value="">Todos</option>
									<option value="activa">Activa</option>
									<option value="inactiva">Inactiva</option>
								</select>
							</div>

							{/* Por Defecto */}
							<div>
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Por Defecto
								</label>
								<select
									value={filtros.es_default}
									onChange={(e) => setFiltros({ ...filtros, es_default: e.target.value })}
									className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
								>
									<option value="">Todas</option>
									<option value="si">Sí</option>
									<option value="no">No</option>
								</select>
							</div>

							{/* Favorita */}
							<div>
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Favorita
								</label>
								<select
									value={filtros.es_favorita}
									onChange={(e) => setFiltros({ ...filtros, es_favorita: e.target.value })}
									className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
								>
									<option value="">Todas</option>
									<option value="si">Sí</option>
									<option value="no">No</option>
								</select>
							</div>

							{/* Actividad Económica */}
							<div>
								<label className="block text-xs font-medium text-gray-700 mb-1">
									Actividad Económica
								</label>
								<input
									type="text"
									value={filtros.actividad_economica}
									onChange={(e) => setFiltros({ ...filtros, actividad_economica: e.target.value })}
									placeholder="Buscar actividad..."
									className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
								/>
							</div>

							{/* Botón Limpiar */}
							<div className="md:col-span-2 lg:col-span-5 flex justify-end">
								<button
									onClick={limpiarFiltros}
									className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
								>
									Limpiar Filtros
								</button>
							</div>
						</div>
					)}
				</div>

				{/* Grid/Tabla */}
				{empresas.length === 0 ? (
					<div className="bg-white rounded-lg shadow-sm p-12 text-center">
						<BuildingOfficeIcon className="h-16 w-16 text-gray-300 mx-auto mb-4" />
						<h3 className="text-lg font-medium text-gray-900 mb-2">No tienes empresas registradas</h3>
						<p className="text-gray-600 mb-6">
							{user?.profile?.rnc_empresa 
								? `Tienes datos de empresa en tu perfil (RNC: ${user.profile.rnc_empresa}). Puedes crear una empresa automáticamente desde estos datos o crear una nueva manualmente.`
								: 'Crea tu primera empresa para comenzar a gestionar formularios DGII'
							}
						</p>
						<div className="flex items-center justify-center gap-4">
							{user?.profile?.rnc_empresa && (
								<button
									onClick={handleCrearDesdePerfil}
									className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
								>
									<CheckIcon className="h-5 w-5" />
									Crear desde Perfil
								</button>
							)}
							<button
								onClick={() => handleOpenModal()}
								className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
							>
								<PlusIcon className="h-5 w-5" />
								Crear Nueva Empresa
							</button>
						</div>
					</div>
				) : (
					<div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
						<div className="overflow-x-auto">
							<table className="min-w-full divide-y divide-gray-200">
								<thead className="bg-gray-50">
									<tr>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Nombre
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											RNC
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Actividad Económica
										</th>
										<th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
											Estado
										</th>
										<th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
											Por Defecto
										</th>
										<th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
											Favorita
										</th>
										<th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
											Acciones
										</th>
									</tr>
								</thead>
								<tbody className="bg-white divide-y divide-gray-200">
									{empresasFiltradas.length === 0 ? (
										<tr>
											<td colSpan="7" className="px-6 py-8 text-center text-gray-500">
												No se encontraron empresas con los filtros aplicados
											</td>
										</tr>
									) : (
										empresasFiltradas.map((empresa) => (
											<tr key={empresa.id} className="hover:bg-gray-50">
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm font-medium text-gray-900">
														{empresa.nombre}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<div className="text-sm text-gray-900">{empresa.rnc}</div>
												</td>
												<td className="px-6 py-4">
													<div className="text-sm text-gray-900 max-w-md truncate" title={empresa.actividad_economica || 'N/A'}>
														{empresa.actividad_economica || 'N/A'}
													</div>
												</td>
												<td className="px-6 py-4 whitespace-nowrap">
													<span
														className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
															empresa.activa
																? 'bg-green-100 text-green-800'
																: 'bg-red-100 text-red-800'
														}`}
													>
														{empresa.activa ? 'Activa' : 'Inactiva'}
													</span>
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-center">
													{empresa.es_default ? (
														<StarIconSolid className="h-5 w-5 text-blue-600 mx-auto" title="Empresa por defecto" />
													) : (
														<button
															onClick={() => handleSetDefault(empresa.id)}
															className="p-1 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors mx-auto"
															title="Establecer como por defecto"
														>
															<StarIcon className="h-4 w-4" />
														</button>
													)}
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-center">
													<button
														onClick={() => handleToggleFavorite(empresa.id)}
														className={`p-1 rounded transition-colors mx-auto ${
															empresa.es_favorita
																? 'text-yellow-500 hover:text-yellow-600'
																: 'text-gray-400 hover:text-yellow-500 hover:bg-yellow-50'
														}`}
														title={empresa.es_favorita ? 'Quitar de favoritas' : 'Marcar como favorita'}
													>
														{empresa.es_favorita ? (
															<StarIconSolid className="h-5 w-5" />
														) : (
															<StarIcon className="h-4 w-4" />
														)}
													</button>
												</td>
												<td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
													<div className="flex items-center justify-end gap-2">
														<button
															onClick={() => handleOpenModal(empresa)}
															className="p-2 text-gray-600 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
															title="Editar"
														>
															<PencilIcon className="h-4 w-4" />
														</button>
							{isAdminOrContador() && (
														<button
															onClick={() => handleOpenReasignarModal(empresa)}
															className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
															title="Reasignar empresa"
														>
															<ArrowRightIcon className="h-4 w-4" />
														</button>
													)}
													{!isCliente() && (
														<button
															onClick={() => handleOpenAccesoModal(empresa)}
															className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
															title="Gestionar acceso de clientes"
														>
															<UserPlusIcon className="h-4 w-4" />
														</button>
													)}
														<button
															onClick={() => handleDelete(empresa.id)}
															className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
															title="Eliminar"
														>
															<TrashIcon className="h-4 w-4" />
														</button>
													</div>
												</td>
											</tr>
										))
									)}
								</tbody>
							</table>
						</div>
						{empresasFiltradas.length > 0 && (
							<div className="px-6 py-3 bg-gray-50 border-t border-gray-200 text-sm text-gray-600">
								Mostrando {empresasFiltradas.length} de {empresas.length} empresas
							</div>
						)}
					</div>
				)}

				{/* Modal Crear/Editar */}
				{showModal && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
						<div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
							<div className="p-6">
								<div className="flex items-center justify-between mb-6">
									<h2 className="text-2xl font-bold text-gray-900">
										{editingEmpresa ? 'Editar Empresa' : 'Crear Empresa'}
									</h2>
									<button
										onClick={handleCloseModal}
										className="text-gray-400 hover:text-gray-600 transition-colors"
									>
										<XMarkIcon className="h-6 w-6" />
									</button>
								</div>

								<form onSubmit={handleSubmit} className="space-y-6">
									{/* Mostrar errores generales */}
									{errors._general && (
										<div className="bg-red-50 border border-red-200 rounded-lg p-3">
											<p className="text-sm text-red-600">{errors._general}</p>
										</div>
									)}
									{errors.non_field_errors && (
										<div className="bg-red-50 border border-red-200 rounded-lg p-3">
											{Array.isArray(errors.non_field_errors) ? (
												errors.non_field_errors.map((error, idx) => (
													<p key={idx} className="text-sm text-red-600">{error}</p>
												))
											) : (
												<p className="text-sm text-red-600">{errors.non_field_errors}</p>
											)}
										</div>
									)}
									
									{/* Nombre */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Nombre de la Empresa *
										</label>
										<input
											type="text"
											value={formData.nombre}
											onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
											className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
												errors.nombre ? 'border-red-300' : 'border-gray-300'
											}`}
											required
										/>
										{errors.nombre && (
											<p className="mt-1 text-sm text-red-600">
												{Array.isArray(errors.nombre) ? errors.nombre[0] : errors.nombre}
											</p>
										)}
									</div>

									{/* RNC */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											RNC *
										</label>
										<input
											type="text"
											value={formData.rnc}
											onChange={(e) => {
												// Solo permitir números
												const numbers = e.target.value.replace(/\D/g, '');
												setFormData({ ...formData, rnc: numbers });
											}}
											placeholder="123456789 o 12345678901"
											maxLength={11}
											className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
												errors.rnc ? 'border-red-300' : 'border-gray-300'
											}`}
											required
										/>
										{errors.rnc && (
											<p className="mt-1 text-sm text-red-600">
												{Array.isArray(errors.rnc) ? errors.rnc[0] : errors.rnc}
											</p>
										)}
									</div>

									{/* Actividad Económica */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Actividad Económica
										</label>
										<ActividadEconomicaSelector
											value={formData.actividad_economica}
											onChange={(actividad) => {
												if (actividad) {
													setFormData({ 
														...formData, 
														actividad_economica: actividad.codigo + ' - ' + actividad.descripcion 
													});
												} else {
													setFormData({ ...formData, actividad_economica: '' });
												}
											}}
											onAddNew={(searchTerm) => {
												// Abrir modal para crear nueva actividad
												setNuevaActividadData({
													codigo: '',
													descripcion: searchTerm,
													literal_grupo: '',
													descripcion_grupo: ''
												});
												setShowActividadModal(true);
											}}
											showGrupos={false}
											placeholder="Buscar actividad económica..."
										/>
										{errors.actividad_economica && (
											<p className="mt-1 text-sm text-red-600">{errors.actividad_economica}</p>
										)}
									</div>

									{/* Teléfono */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Teléfono
										</label>
										<input
											type="tel"
											value={formData.telefono}
											onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
											placeholder="(809) 123-4567"
											className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
												errors.telefono ? 'border-red-300' : 'border-gray-300'
											}`}
										/>
										{errors.telefono && (
											<p className="mt-1 text-sm text-red-600">{errors.telefono}</p>
										)}
									</div>

									{/* Email */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Correo Electrónico
										</label>
										<input
											type="email"
											value={formData.email}
											onChange={(e) => setFormData({ ...formData, email: e.target.value })}
											placeholder="empresa@ejemplo.com"
											className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
												errors.email ? 'border-red-300' : 'border-gray-300'
											}`}
										/>
										{errors.email && (
											<p className="mt-1 text-sm text-red-600">{errors.email}</p>
										)}
									</div>

									{/* Dirección */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Dirección
										</label>
										<textarea
											value={formData.direccion}
											onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
											placeholder="Dirección completa de la empresa"
											rows={3}
											className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
												errors.direccion ? 'border-red-300' : 'border-gray-300'
											}`}
										/>
										{errors.direccion && (
											<p className="mt-1 text-sm text-red-600">{errors.direccion}</p>
										)}
									</div>

									{/* Régimen Fiscal */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Régimen Fiscal
										</label>
										<select
											value={formData.regimen_fiscal}
											onChange={(e) => setFormData({ ...formData, regimen_fiscal: e.target.value })}
											className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
												errors.regimen_fiscal ? 'border-red-300' : 'border-gray-300'
											}`}
										>
											<option value="">Seleccionar régimen fiscal</option>
											<option value="general">Régimen General</option>
											<option value="simplificado">Régimen Simplificado</option>
											<option value="especial">Régimen Especial</option>
											<option value="agricola">Régimen Agrícola</option>
											<option value="turismo">Régimen Turismo</option>
										</select>
										{errors.regimen_fiscal && (
											<p className="mt-1 text-sm text-red-600">{errors.regimen_fiscal}</p>
										)}
									</div>

									{/* Fecha de Cierre */}
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Fecha de Cierre del Período Fiscal
										</label>
										<input
											type="date"
											value={formData.fecha_cierre}
											onChange={(e) => setFormData({ ...formData, fecha_cierre: e.target.value })}
											className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary ${
												errors.fecha_cierre ? 'border-red-300' : 'border-gray-300'
											}`}
										/>
										<p className="mt-1 text-xs text-gray-500">
											Último día del mes de cierre del período fiscal
										</p>
										{errors.fecha_cierre && (
											<p className="mt-1 text-sm text-red-600">{errors.fecha_cierre}</p>
										)}
									</div>

							{/* Selector de Usuario (admin y contador al crear) */}
							{isAdminOrContador() && !editingEmpresa && (
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Asignar a Usuario
										</label>
										{loadingUsuarios ? (
											<div className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg bg-gray-50">
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
												<span className="text-sm text-gray-500">Cargando usuarios...</span>
											</div>
										) : (
											<select
												value={formData.usuario}
												onChange={(e) => setFormData({ ...formData, usuario: e.target.value })}
												className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
											>
												{usuarios.map((u) => (
													<option key={u.id} value={u.id}>
														{u.id === user?.id
															? `${u.username} (yo) — ${u.email}`
															: `${u.username} — ${u.email}${u.profile?.role_display ? ` [${u.profile.role_display}]` : ''}`
														}
													</option>
												))}
												{usuarios.length === 0 && (
													<option value={user?.id || ''}>
														{user?.username} (yo) — {user?.email}
													</option>
												)}
											</select>
										)}
									</div>
								)}

									{/* Estado Activa */}
									<div className="flex items-center gap-3">
										<input
											type="checkbox"
											id="activa"
											checked={formData.activa}
											onChange={(e) => setFormData({ ...formData, activa: e.target.checked })}
											className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
										/>
										<label htmlFor="activa" className="text-sm font-medium text-gray-700">
											Empresa activa
										</label>
									</div>

									{/* Botones */}
									<div className="flex items-center gap-3 pt-4">
										<button
											type="button"
											onClick={handleCloseModal}
											className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
										>
											Cancelar
										</button>
										<button
											type="submit"
											className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
										>
											<CheckIcon className="h-5 w-5" />
											{editingEmpresa ? 'Actualizar' : 'Crear'}
										</button>
									</div>
								</form>
							</div>
						</div>
					</div>
				)}

			{/* Modal para crear nueva actividad económica (z-[60] para aparecer sobre el modal de empresa z-50) */}
			{showActividadModal && (
				<div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
						<div className="bg-white rounded-lg shadow-xl max-w-md w-full">
							<div className="p-6">
								<div className="flex items-center justify-between mb-4">
									<h3 className="text-lg font-bold text-gray-900">Crear Nueva Actividad Económica</h3>
									<button
										onClick={() => {
											setShowActividadModal(false);
											setNuevaActividadData({ codigo: '', descripcion: '', literal_grupo: '', descripcion_grupo: '' });
										}}
										className="text-gray-400 hover:text-gray-600"
									>
										<XMarkIcon className="h-5 w-5" />
									</button>
								</div>
								<div className="space-y-4">
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Código <span className="text-red-500">*</span>
										</label>
										<input
											type="text"
											value={nuevaActividadData.codigo}
											onChange={(e) => {
												const value = e.target.value.replace(/\D/g, '').slice(0, 6);
												setNuevaActividadData({ ...nuevaActividadData, codigo: value });
											}}
											placeholder="0001 o 151123 (máximo 6 dígitos)"
											maxLength={6}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
										/>
									</div>
									<div>
										<label className="block text-sm font-medium text-gray-700 mb-1">
											Descripción <span className="text-red-500">*</span>
										</label>
										<textarea
											value={nuevaActividadData.descripcion}
											onChange={(e) => setNuevaActividadData({ ...nuevaActividadData, descripcion: e.target.value })}
											placeholder="Descripción de la actividad..."
											rows={3}
											className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
										/>
									</div>
									<div className="grid grid-cols-2 gap-4">
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Literal Grupo
											</label>
											<input
												type="text"
												value={nuevaActividadData.literal_grupo}
												onChange={(e) => setNuevaActividadData({ ...nuevaActividadData, literal_grupo: e.target.value.toUpperCase() })}
												placeholder="A-Q (Opcional)"
												maxLength={1}
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
											/>
										</div>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Descripción Grupo
											</label>
											<input
												type="text"
												value={nuevaActividadData.descripcion_grupo}
												onChange={(e) => setNuevaActividadData({ ...nuevaActividadData, descripcion_grupo: e.target.value })}
												placeholder="Opcional"
												className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary"
											/>
										</div>
									</div>
									<div className="flex gap-3 pt-4">
										<button
											type="button"
											onClick={() => {
												setShowActividadModal(false);
												setNuevaActividadData({ codigo: '', descripcion: '', literal_grupo: '', descripcion_grupo: '' });
											}}
											className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
										>
											Cancelar
										</button>
										<button
											type="button"
											onClick={async () => {
												if (!nuevaActividadData.codigo || !nuevaActividadData.descripcion) {
													alert('Código y descripción son requeridos');
													return;
												}
												try {
													await dgiiApi.createActividadEconomica({
														...nuevaActividadData,
														activa: true
													});
													// Actualizar el campo de actividad económica en el formulario
													setFormData({
														...formData,
														actividad_economica: nuevaActividadData.codigo + ' - ' + nuevaActividadData.descripcion
													});
													setShowActividadModal(false);
													setNuevaActividadData({ codigo: '', descripcion: '', literal_grupo: '', descripcion_grupo: '' });
													alert('Actividad económica creada exitosamente');
												} catch (err) {
													console.error('Error creando actividad:', err);
													alert('Error: ' + (err.response?.data?.detail || err.message));
												}
											}}
											className="flex-1 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
										>
											Crear
										</button>
									</div>
								</div>
							</div>
						</div>
					</div>
				)}

				{/* Modal Reasignar Empresa */}
				{showReasignarModal && empresaReasignar && (
					<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
						<div className="bg-white rounded-lg shadow-xl max-w-md w-full">
							<div className="p-6">
								<div className="flex items-center justify-between mb-6">
									<h2 className="text-2xl font-bold text-gray-900">
										Reasignar Empresa
									</h2>
									<button
										onClick={handleCloseReasignarModal}
										className="text-gray-400 hover:text-gray-600 transition-colors"
									>
										<XMarkIcon className="h-6 w-6" />
									</button>
								</div>

								<div className="space-y-4 mb-6">
									<div className="bg-gray-50 p-4 rounded-lg">
										<p className="text-sm font-medium text-gray-700 mb-2">Empresa a reasignar:</p>
										<p className="text-lg font-semibold text-gray-900">{empresaReasignar.nombre}</p>
										<p className="text-sm text-gray-600">RNC: {empresaReasignar.rnc}</p>
										<p className="text-sm text-gray-600">Usuario actual: {empresaReasignar.usuario_nombre || 'N/A'}</p>
									</div>

									<div>
										<label className="block text-sm font-medium text-gray-700 mb-2">
											Seleccionar nuevo usuario *
										</label>
									{loadingUsuarios ? (
										<div className="text-center py-4">
											<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto"></div>
											<p className="mt-2 text-sm text-gray-500">Cargando usuarios...</p>
										</div>
									) : errorUsuarios === 'sin_usuarios' ? (
										<div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
											<div className="flex-shrink-0 text-amber-500 text-xl">⚠️</div>
											<div>
												<p className="text-sm font-semibold text-amber-800">No tienes usuarios disponibles para reasignar</p>
												<p className="text-sm text-amber-700 mt-1">
													Primero debes crear al menos un usuario desde <strong>Gestión de Usuarios</strong> para poder reasignar empresas.
												</p>
											</div>
										</div>
									) : (
										<select
											value={usuarioSeleccionado}
											onChange={(e) => setUsuarioSeleccionado(e.target.value)}
											className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
										>
											<option value="">Seleccione un usuario</option>
											{usuarios.map((usuario) => (
												<option key={usuario.id} value={usuario.id}>
													{usuario.username} ({usuario.email}) - {usuario.profile?.role_display || 'Sin rol'}
												</option>
											))}
										</select>
									)}
									</div>

									<div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
										<p className="text-sm text-yellow-800">
											⚠️ <strong>Advertencia:</strong> Esta acción quitará la empresa del usuario actual y la asignará al nuevo usuario seleccionado.
										</p>
									</div>
								</div>

								{/* Botones */}
								<div className="flex items-center gap-3 pt-4">
									<button
										type="button"
										onClick={handleCloseReasignarModal}
										className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
									>
										Cancelar
									</button>
									<button
										type="button"
										onClick={handleReasignar}
										disabled={!usuarioSeleccionado || loading}
										className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
									>
										<ArrowRightIcon className="h-5 w-5" />
										{loading ? 'Reasignando...' : 'Reasignar'}
									</button>
								</div>
							</div>
						</div>
					</div>
				)}

			{/* Modal Gestionar Acceso de Clientes */}
			{showAccesoModal && empresaAcceso && (
				<div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
					<div className="bg-white rounded-lg shadow-xl max-w-md w-full">
						<div className="p-6">
							<div className="flex items-center justify-between mb-5">
								<div>
									<h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
										<UserPlusIcon className="h-5 w-5 text-green-600" />
										Acceso de Clientes
									</h2>
									<p className="text-sm text-gray-500 mt-0.5">{empresaAcceso.nombre}</p>
								</div>
								<button
									onClick={handleCloseAccesoModal}
									className="text-gray-400 hover:text-gray-600 transition-colors"
								>
									<XMarkIcon className="h-6 w-6" />
								</button>
							</div>

							{/* Accesos actuales */}
							<div className="mb-5">
								<h3 className="text-sm font-medium text-gray-700 mb-2">Clientes con acceso</h3>
								{loadingAccesos ? (
									<div className="flex items-center gap-2 py-3 text-sm text-gray-500">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
										Cargando...
									</div>
								) : accesos.length === 0 ? (
									<p className="text-sm text-gray-400 italic py-2">Sin clientes con acceso compartido</p>
								) : (
									<ul className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
										{accesos.map((acceso) => (
											<li key={acceso.id} className="flex items-center justify-between px-4 py-2.5 bg-white hover:bg-gray-50">
												<div>
													<p className="text-sm font-medium text-gray-900">{acceso.usuario_nombre || acceso.usuario_username}</p>
													<p className="text-xs text-gray-500">{acceso.usuario_email}</p>
												</div>
												<button
													onClick={() => handleRevocarAcceso(acceso.usuario)}
													className="p-1 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
													title="Quitar acceso"
												>
													<XMarkIcon className="h-4 w-4" />
												</button>
											</li>
										))}
									</ul>
								)}
							</div>

							{/* Agregar nuevo acceso */}
							<div>
								<h3 className="text-sm font-medium text-gray-700 mb-2">Agregar cliente</h3>
								{loadingUsuariosCliente ? (
									<div className="flex items-center gap-2 py-2 text-sm text-gray-500">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
										Cargando usuarios...
									</div>
								) : usuariosCliente.filter(u => !accesos.some(a => a.usuario === u.id)).length === 0 ? (
									<p className="text-sm text-gray-400 italic">No hay clientes disponibles para agregar</p>
								) : (
									<div className="flex gap-2">
										<select
											value={usuarioAccesoSeleccionado}
											onChange={(e) => setUsuarioAccesoSeleccionado(e.target.value)}
											className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
										>
											<option value="">Seleccionar cliente...</option>
											{usuariosCliente
												.filter(u => !accesos.some(a => a.usuario === u.id))
												.map(u => (
													<option key={u.id} value={u.id}>
														{u.username} — {u.email}
													</option>
												))
											}
										</select>
										<button
											onClick={handleOtorgarAcceso}
											disabled={!usuarioAccesoSeleccionado || savingAcceso}
											className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
										>
											{savingAcceso ? (
												<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
											) : (
												<CheckIcon className="h-4 w-4" />
											)}
											Agregar
										</button>
									</div>
								)}
							</div>

							<div className="mt-5 flex justify-end">
								<button
									onClick={handleCloseAccesoModal}
									className="px-4 py-2 border border-gray-300 text-gray-700 text-sm rounded-lg hover:bg-gray-50 transition-colors"
								>
									Cerrar
								</button>
							</div>
						</div>
					</div>
				</div>
			)}
		</div>
		</div>
	);
}
